import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Icon } from '@cbrebuild/blocks';
import { toast } from '../../redux/actions/toasts-actions';
import userEventService from '../../services/user-event-service';
import DealIQLogo from '../../assets/images/svg/loading-icon.svg';
import LoadMore from '../../nucleus/load-more/load-more';
import Avatar from '../../nucleus/avatar/avatar';
import './admin-manage-pool-accounts.scss';

/**
 * Generic component for uploading and managing Excel data (Pool Accounts, FUMOs, etc.)
 * Reuses same UI and logic, just needs different configuration
 */
const AdminManageDataUpload = ({ 
  config,
  ComparisonTableComponent,
  extraHeaderActions,
  toast: showToast 
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [showViewAll, setShowViewAll] = useState(false);
  const [viewAllData, setViewAllData] = useState([]);
  const [viewAllLoading, setViewAllLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const fileInputRef = useRef(null);

  // Inline edit state (opt-in via config.viewAllConfig.editable)
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [savingRowKey, setSavingRowKey] = useState(null);

  // Per-row audit history state (opt-in via config.viewAllConfig.historyService)
  const [historyRowKey, setHistoryRowKey] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);

  const pageSize = 25;
  const historyPageSize = 10;

  // Whether the View All table renders an actions column (edit/history controls)
  const hasViewAllActionsColumn = !!(config.viewAllConfig && (config.viewAllConfig.editable || config.viewAllConfig.historyService));
  const viewAllColumnSpan = (config.viewAllConfig?.columns.length || 0) + (hasViewAllActionsColumn ? 1 : 0);

  // Tracks the latest editingRowKey synchronously so async saves can detect if the user
  // has since switched to editing a different row, without nesting setState calls.
  const editingRowKeyRef = useRef(editingRowKey);
  useEffect(() => { editingRowKeyRef.current = editingRowKey; }, [editingRowKey]);

  // Build a stable row key/identity, supporting both single-field (idField) and
  // composite-key (idFields, e.g. market+property_type) natural keys.
  const getRowIdentity = (record) => {
    if (config.viewAllConfig.idFields) {
      const identity = {};
      config.viewAllConfig.idFields.forEach(field => { identity[field] = record[field]; });
      return identity;
    }
    return { [config.viewAllConfig.idField]: record[config.viewAllConfig.idField] };
  };

  const getRowKey = (record) => {
    if (config.viewAllConfig.idFields) {
      return config.viewAllConfig.idFields.map(field => record[field]).join('||');
    }
    return record[config.viewAllConfig.idField];
  };

  // Fetch file info on component mount
  useEffect(() => {
    if (config.getFileInfoService) {
      fetchFileInfo();
    }
  }, []);

  // Fetch latest uploaded file info
  const fetchFileInfo = async () => {
    if (!config.getFileInfoService) return;
    
    try {
      const info = await config.getFileInfoService();
      setFileInfo(info);
    } catch (error) {
      console.error('Error fetching file info');
      // Don't show error toast, just fail silently
    }
  };

  // Handle file download
  const handleDownload = async () => {
    if (!config.downloadFileService) return;
    
    setDownloadingFile(true);
    
    // Track file download
    userEventService.trackEvent({
      eventCategory: 'Admin Capabilities',
      eventAction: 'admin_settings_file_download',
      eventLabel: 'admin_manage_settings',
    });
    
    try {
      const filename = fileInfo?.filename || 'download.xlsx';
      await config.downloadFileService(filename);
      showToast('File downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading file');
      showToast('Failed to download file. Please try again.', 'error');
    } finally {
      setDownloadingFile(false);
    }
  };

  // Fetch paginated view all data
  const fetchViewAllData = async (page, search, append = false) => {
    if (!config.viewAllConfig) return;
    
    if (append) {
      setLoadingMore(true);
    } else {
      setViewAllLoading(true);
    }
    
    try {
      const response = await config.viewAllConfig.fetchAllService(page, pageSize, search);
      if (response.error) {
        showToast(response.error, 'error');
        if (!append) {
          setViewAllData([]);
          setTotalCount(0);
        }
      } else {
        if (append) {
          setViewAllData(prev => [...prev, ...(response.results || [])]);
        } else {
          setViewAllData(response.results || []);
        }
        setTotalCount(response.count || 0);
      }
    } catch (error) {
      console.error('Error fetching records');
      showToast(`Error loading ${config.viewAllConfig.entityName}`, 'error');
      if (!append) {
        setViewAllData([]);
        setTotalCount(0);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setViewAllLoading(false);
      }
    }
  };

  // Fetch data when showViewAll or search changes (not on page change)
  useEffect(() => {
    if (showViewAll) {
      setViewAllData([]);
      setCurrentPage(1);
      fetchViewAllData(1, searchTerm);
      
      // Track view all action
      userEventService.trackEvent({
        eventCategory: 'Admin Capabilities',
        eventAction: 'admin_settings_view_all',
        eventLabel: 'admin_manage_settings',
      });
    }
  }, [showViewAll, searchTerm]);

  // Handle load more
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchViewAllData(nextPage, searchTerm, true);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'application/csv'];
      const validExtensions = ['.xls', '.xlsx', '.csv'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension) && !validTypes.includes(file.type)) {
        showToast('Invalid file format. Please upload an Excel (.xlsx or .xls) or CSV (.csv) file.', 'error');
        setUploadedFile(null);
        return;
      }

      setUploadedFile(file);
      setComparisonData(null);
    }
  };

  // Handle file upload and comparison
  const handleUpload = async () => {
    if (!uploadedFile) {
      showToast('Please select a file to upload.', 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await config.uploadService(uploadedFile);

      if (response.error) {
        console.error('Upload validation error');
        showToast(response.error, 'error');
        setComparisonData(null);
      } else {
        setComparisonData(response);
        
        // Track successful upload
        userEventService.trackEvent({
          eventCategory: 'Admin Capabilities',
          eventAction: 'admin_settings_file_upload',
          eventLabel: 'admin_manage_settings',
        });
        
        showToast(`File processed successfully. ${response.total_records} records found.`, 'success');
      }
    } catch (err) {
      console.error('Upload error:');
      
      let errorMessage = 'An error occurred while uploading the file.';
      
      try {
        const errorData = err?.response || err || {};
        
        if (errorData.error) {
          errorMessage = String(errorData.error);
          if (errorData.details) errorMessage += `\n\nDetails: ${String(errorData.details)}`;
          if (errorData.note) errorMessage += `\n\n${String(errorData.note)}`;
          if (Array.isArray(errorData.required_columns) && errorData.required_columns.length > 0) {
            errorMessage += `\n\nRequired columns: ${errorData.required_columns.join(', ')}`;
          }
          if (Array.isArray(errorData.found_columns) && errorData.found_columns.length > 0) {
            errorMessage += `\n\nFound in your file: ${errorData.found_columns.join(', ')}`;
          }
        } else if (errorData.message) {
          errorMessage = String(errorData.message);
        } else if (err?.message) {
          errorMessage = String(err.message);
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
      } catch (parseError) {
        console.error('Error parsing error response:');
        errorMessage = 'An unexpected error occurred while uploading the file.';
      }
      
      errorMessage += '\n\nPlease check your file format and try again.';
      showToast(errorMessage, 'error');
      setComparisonData(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk update
  const handleBulkUpdate = async (updates) => {
    setLoading(true);

    try {
      const response = await config.bulkUpdateService(updates, uploadedFile);

      if (response.error) {
        showToast(response.error, 'error');
      } else {
        // Backend returns summary object, not results
        const { 
          success_count, 
          created_count, 
          updated_count, 
          ignored_count, 
          error_count,
          errors 
        } = response.summary || response.results || {};
        
        // Track bulk update
        userEventService.trackEvent({
          eventCategory: 'Admin Capabilities',
          eventAction: 'admin_settings_bulk_update',
          eventLabel: 'admin_manage_settings',
        });
        
        const messageParts = [];
        if (created_count > 0) messageParts.push(`${created_count} new record(s) created`);
        if (updated_count > 0) messageParts.push(`${updated_count} existing record(s) updated`);
        if (ignored_count > 0) messageParts.push(`${ignored_count} record(s) ignored`);
        
        let message = messageParts.length > 0 
          ? `Update completed: ${messageParts.join(', ')}.`
          : 'No changes made.';
        
        if (error_count > 0) {
          message += ` ${error_count} error(s) occurred.`;
          if (errors && errors.length > 0) console.error('Update errors');
          showToast(message, 'warning');
        } else {
          showToast(message, 'success');
        }
        
        // Refresh file info after successful update
        await fetchFileInfo();
        
        setComparisonData(null);
        setUploadedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Bulk update error occurred');
      showToast(`An error occurred while updating ${config.entityName}. Please try again.`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel/reset
  const handleCancel = () => {
    setUploadedFile(null);
    setComparisonData(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle starting an inline edit of a View All row (opt-in via config.viewAllConfig.editable)
  const handleStartEdit = (record) => {
    const draft = {};
    (config.viewAllConfig.editFields || []).forEach(field => { draft[field] = record[field]; });
    setEditingRowKey(getRowKey(record));
    setEditDraft(draft);
  };

  const handleCancelEdit = () => {
    setEditingRowKey(null);
    setEditDraft({});
  };

  const handleEditFieldChange = (field, value) => {
    setEditDraft(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async (record) => {
    const rowKey = getRowKey(record);
    setSavingRowKey(rowKey);

    try {
      const identity = getRowIdentity(record);
      const updated = await config.viewAllConfig.updateService(identity, editDraft);

      if (updated && updated.error) {
        showToast(updated.error, 'error');
      } else {
        setViewAllData(prev => prev.map(r => (getRowKey(r) === rowKey ? { ...r, ...updated } : r)));
        showToast(`${config.viewAllConfig.entityName} updated successfully.`, 'success');

        userEventService.trackEvent({
          eventCategory: 'Admin Capabilities',
          eventAction: 'admin_settings_inline_edit',
          eventLabel: 'admin_manage_settings',
        });

        // Only clear the shared edit state if the user hasn't since started editing a different row
        if (editingRowKeyRef.current === rowKey) {
          setEditingRowKey(null);
          setEditDraft({});
        }
      }
    } catch (error) {
      console.error('Error updating record');
      showToast(`Failed to update ${config.viewAllConfig.entityName}. Please try again.`, 'error');
    } finally {
      setSavingRowKey(null);
    }
  };

  // Handle toggling the per-row audit history panel (opt-in via config.viewAllConfig.historyService)
  const handleToggleHistory = async (record) => {
    const rowKey = getRowKey(record);

    if (historyRowKey === rowKey) {
      setHistoryRowKey(null);
      setHistoryData([]);
      setHistoryLoadingMore(false);
      return;
    }

    setHistoryRowKey(rowKey);
    setHistoryData([]);
    setHistoryPage(1);
    setHistoryLoading(true);
    setHistoryLoadingMore(false);

    try {
      const identity = getRowIdentity(record);
      const response = await config.viewAllConfig.historyService(identity, 1, historyPageSize);
      setHistoryData(response.results || []);
      setHistoryTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching history');
      showToast('Failed to load history. Please try again.', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleLoadMoreHistory = async (record) => {
    setHistoryLoadingMore(true);

    try {
      const identity = getRowIdentity(record);
      const nextPage = historyPage + 1;
      const response = await config.viewAllConfig.historyService(identity, nextPage, historyPageSize);
      setHistoryData(prev => [...prev, ...(response.results || [])]);
      setHistoryPage(nextPage);
    } catch (error) {
      console.error('Error fetching more history');
      showToast('Failed to load more history. Please try again.', 'error');
    } finally {
      setHistoryLoadingMore(false);
    }
  };

  return (
    <div className="admin-pool-accounts-container">
      {(!comparisonData || showViewAll) && (
        <div className="admin-pool-accounts-header">
          <div className="admin-pool-accounts-header-content">
            {!showViewAll ? (
              <>
                <div>
                  <h1>{config.title}</h1>
                  <p className="admin-pool-accounts-description">
                    {config.description}
                  </p>
                </div>
                <div className="admin-header-actions">
                  {extraHeaderActions}
                  {config.viewAllConfig && (
                    <Button
                      onClick={() => setShowViewAll(true)}
                      variant="secondary"
                      className="admin-view-all-button"
                    >
                      View All Records
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div>
                  <h1>View All {config.viewAllConfig?.entityName || 'Records'}</h1>
                  <p className="admin-pool-accounts-description">
                    Browse and search through all existing {config.viewAllConfig?.entityName?.toLowerCase() || 'records'}.
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setShowViewAll(false);
                    setComparisonData(null);
                  }}
                  variant="secondary"
                  className="admin-view-all-button"
                >
                  Upload File
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="spinner-container">
          <div className="loading-spinner">
            <img alt="Loading" src={DealIQLogo} />
          </div>
        </div>
      )}

      {/* Upload section - only show if not viewing all records */}
      {!showViewAll && !comparisonData && !loading && (
        <div className="admin-pool-accounts-upload-section">
          <div className="admin-pool-accounts-upload-card">
            <div className="upload-card-content">
              <div className="upload-card-left">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <h2 style={{ margin: 0 }}>{config.uploadTitle}</h2>
                  {config.templateDownload?.show && (
                    <Button
                      onClick={config.templateDownload.onDownload}
                      variant="secondary"
                      className="admin-download-template-button"
                    >
                      <Icon iconName="download" />
                      {config.templateDownload.buttonText || 'Download Template'}
                    </Button>
                  )}
                </div>
                <p className="admin-pool-accounts-note">
                  <Icon iconName="info-circle" />
                  <span>Excel (.xlsx, .xls) and CSV (.csv) files are supported.</span>
                </p>

                <div className="admin-pool-accounts-file-input-wrapper">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="admin-pool-accounts-file-input"
                    id={`${config.fileInputId}-file-input`}
                  />
                  <label htmlFor={`${config.fileInputId}-file-input`} className="admin-pool-accounts-file-label">
                    <div className="file-label-main">
                      <Icon iconName="upload" />
                      <span>{uploadedFile ? uploadedFile.name : 'Click here to upload'}</span>
                    </div>
                  </label>
                </div>

                <div className="admin-pool-accounts-upload-actions">
                  <Button
                    onClick={handleUpload}
                    disabled={!uploadedFile || loading}
                    variant="primary"
                  >
                    {config.uploadButtonText || 'Upload and Compare'}
                  </Button>
                  {uploadedFile && (
                    <Button
                      onClick={handleCancel}
                      disabled={loading}
                      variant="secondary"
                    >
                      Cancel
                    </Button>
              )}
                </div>
              </div>
              
              {/* Vertical Separator - only show for default file info panel */}
              {!config.customRightPanel && fileInfo && fileInfo.exists && fileInfo.filename && (
                <div className="admin-file-info-separator"></div>
              )}
              
              {/* Custom Right Panel (e.g., Download Center for Attachment Lookup) */}
              {config.customRightPanel}
              
              {/* Latest Uploaded File Info - Right Side (fallback if no custom panel) */}
              {!config.customRightPanel && fileInfo && fileInfo.exists && fileInfo.filename && (
                <div className="upload-card-right">
                  <h2 className="admin-file-info-header">Latest Uploaded File</h2>
                  <div className="admin-file-info-content">
                    <div className="admin-file-info-filename">
                      <span><Icon iconName="document" /> {fileInfo.filename}</span>
                      <button
                        onClick={handleDownload}
                        disabled={downloadingFile}
                        className="admin-download-icon-button"
                      >
                        <Icon iconName="download" />
                      </button>
                    </div>
                    <div className="admin-file-info-size">
                      {(fileInfo.file_size / 1024).toFixed(2)} KB
                    </div>
                    <div className="admin-file-info-date">
                      <Icon iconName="clock" /> {new Date(fileInfo.uploaded_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div className="admin-file-info-meta-item admin-file-info-user">
                      <Avatar
                        image={fileInfo.uploaded_by?.avatar}
                        firstName={fileInfo.uploaded_by?.first_name || ''}
                        lastName={fileInfo.uploaded_by?.last_name || ''}
                        sizing="xs"
                        enableHover
                      />
                      <span>{fileInfo.uploaded_by?.email?.work}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comparison section - only show if not viewing all records */}
      {!showViewAll && comparisonData && (
        <div className="admin-pool-accounts-comparison-section">
          {!config.hideComparisonStats && (
            <div className="admin-pool-accounts-summary">
              <div className="admin-comparison-results-header">
                <div>
                  <h1>Comparison Results</h1>
                  <p>
                    Review the changes detected in your uploaded file and choose which records to accept or ignore.
                  </p>
                </div>
                <div className="admin-comparison-results-actions">
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                    className="admin-view-all-button"
                  >
                    Upload File
                  </Button>
                  {config.viewAllConfig && (
                    <Button
                      onClick={() => setShowViewAll(true)}
                      variant="secondary"
                      className="admin-view-all-button"
                    >
                      View All Records
                    </Button>
                  )}
                </div>
              </div>
              <div className="admin-pool-accounts-summary-stats">
                <div className="admin-pool-accounts-stat">
                  <span className="admin-pool-accounts-stat-label">Total Records:</span>
                  <span className="admin-pool-accounts-stat-value">{comparisonData.total_records}</span>
                </div>
                <div className="admin-pool-accounts-stat admin-pool-accounts-stat-updated">
                  <span className="admin-pool-accounts-stat-label">Updated:</span>
                  <span className="admin-pool-accounts-stat-value">{comparisonData.updated_count}</span>
                </div>
                <div className="admin-pool-accounts-stat admin-pool-accounts-stat-new">
                  <span className="admin-pool-accounts-stat-label">New:</span>
                  <span className="admin-pool-accounts-stat-value">{comparisonData.new_count}</span>
                </div>
                <div className="admin-pool-accounts-stat admin-pool-accounts-stat-unchanged">
                  <span className="admin-pool-accounts-stat-label">Unchanged:</span>
                  <span className="admin-pool-accounts-stat-value">{comparisonData.unchanged_count}</span>
                </div>
              </div>
            </div>
          )}

          <ComparisonTableComponent
            data={comparisonData.results}
            onBulkUpdate={handleBulkUpdate}
            onCancel={handleCancel}
            loading={loading}
            toast={showToast}
            config={config.comparisonConfig || config}
            uploadedFile={uploadedFile}
          />
        </div>
      )}

      {/* View All Records Table - reuse existing voucher history table styles */}
      {showViewAll && config.viewAllConfig && (
        <div className="admin-voucher-history-card">
          {/* Search Bar and Summary in one row */}
          <div className="admin-view-all-header">
            <div className="admin-view-all-search-container">
              <Icon iconName="search" />
              <input
                type="text"
                placeholder={`Search ${config.viewAllConfig.entityName}...`}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <div className="admin-view-all-summary">
              {totalCount > 0 ? `Showing ${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} records` : 'No records found'}
              {searchTerm && ` (filtered by "${searchTerm}")`}
            </div>
          </div>

          {/* Table */}
          <div className="admin-pool-comparison-table-wrapper admin-view-all-table-wrapper">
            <div className="admin-pool-comparison-table-container">
              <table className="admin-pool-comparison-table">
                <thead>
                  <tr>
                    {config.viewAllConfig.columns.map(col => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                    {hasViewAllActionsColumn && (
                      <th className="admin-view-all-col-actions">ACTIONS</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {viewAllLoading ? (
                    <tr>
                      <td colSpan={viewAllColumnSpan} className="admin-view-all-table-empty">
                        <Icon iconName="spinner" spin /> <span>Loading {config.viewAllConfig.entityName}...</span>
                      </td>
                    </tr>
                  ) : viewAllData.length === 0 ? (
                    <tr>
                      <td colSpan={viewAllColumnSpan} className="admin-view-all-table-empty">
                        <Icon iconName="info-circle" /> <span>No {config.viewAllConfig.entityName} found</span>
                      </td>
                    </tr>
                  ) : (
                    viewAllData.map((record, idx) => {
                      const rowKey = getRowKey(record) || idx;
                      const isEditing = config.viewAllConfig.editable && editingRowKey === rowKey;
                      const isSaving = savingRowKey === rowKey;
                      const isShowingHistory = historyRowKey === rowKey;

                      return (
                        <React.Fragment key={rowKey}>
                          <tr>
                            {config.viewAllConfig.columns.map(col => (
                              <td key={col.key}>
                                {isEditing && (config.viewAllConfig.editFields || []).includes(col.key) ? (
                                  <input
                                    type="text"
                                    className="admin-view-all-edit-input"
                                    value={editDraft[col.key] ?? ''}
                                    onChange={(e) => handleEditFieldChange(col.key, e.target.value)}
                                    disabled={isSaving}
                                  />
                                ) : (
                                  record[col.key] === null || record[col.key] === undefined || record[col.key] === ''
                                    ? '-'
                                    : record[col.key]
                                )}
                              </td>
                            ))}
                            {hasViewAllActionsColumn && (
                              <td className="admin-view-all-col-actions">
                                <div className="admin-view-all-row-actions">
                                  {config.viewAllConfig.editable && (
                                    isEditing ? (
                                      <>
                                        <Button
                                          onClick={() => handleSaveEdit(record)}
                                          variant="primary"
                                          size="small"
                                          disabled={isSaving}
                                        >
                                          {isSaving ? 'Saving...' : 'Save'}
                                        </Button>
                                        <Button
                                          onClick={handleCancelEdit}
                                          variant="secondary"
                                          size="small"
                                          disabled={isSaving}
                                        >
                                          Cancel
                                        </Button>
                                      </>
                                    ) : (
                                      <Button
                                        onClick={() => handleStartEdit(record)}
                                        variant="secondary"
                                        size="small"
                                        disabled={savingRowKey !== null}
                                      >
                                        Edit
                                      </Button>
                                    )
                                  )}
                                  {config.viewAllConfig.historyService && (
                                    <Button
                                      onClick={() => handleToggleHistory(record)}
                                      variant="secondary"
                                      size="small"
                                    >
                                      {isShowingHistory ? 'Hide History' : 'View History'}
                                    </Button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                          {isShowingHistory && (
                            <tr className="admin-view-all-history-row">
                              <td colSpan={viewAllColumnSpan}>
                                {historyLoading ? (
                                  <div className="admin-view-all-history-loading">
                                    <Icon iconName="spinner" spin /> <span>Loading history...</span>
                                  </div>
                                ) : historyData.length === 0 ? (
                                  <div className="admin-view-all-history-empty">No history found.</div>
                                ) : (
                                  <>
                                    <table className="admin-view-all-history-table">
                                      <thead>
                                        <tr>
                                          <th>MODIFIED ON</th>
                                          <th>MODIFIED BY</th>
                                          <th>MESSAGE</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {historyData.map(entry => (
                                          <tr key={entry.id}>
                                            <td>{entry.created ? new Date(entry.created).toLocaleString() : '-'}</td>
                                            <td>{entry.admin_user_email || 'System'}</td>
                                            <td>{entry.message}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {historyData.length < historyTotalCount && (
                                      <LoadMore
                                        isLoading={historyLoadingMore}
                                        hasNextPage={historyData.length < historyTotalCount}
                                        handleLoadMore={() => handleLoadMoreHistory(record)}
                                      />
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Load More */}
          <LoadMore
            isLoading={loadingMore}
            hasNextPage={viewAllData.length < totalCount}
            handleLoadMore={handleLoadMore}
          />
        </div>
      )}
    </div>
  );
};

AdminManageDataUpload.propTypes = {
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    uploadTitle: PropTypes.string.isRequired,
    entityName: PropTypes.string.isRequired,
    fileInputId: PropTypes.string.isRequired,
    uploadService: PropTypes.func.isRequired,
    bulkUpdateService: PropTypes.func.isRequired,
  }).isRequired,
  ComparisonTableComponent: PropTypes.elementType.isRequired,
  toast: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  toast,
};

export default connect(null, mapDispatchToProps)(AdminManageDataUpload);
